import type { Route } from '@angular/router';

export interface OrgTeamRoutesOptions {
  /** 팀 구조 화면 제목(h1과 라우트 `title`). 생략하면 화면은 '팀 구조', 문서 제목은 앱 기본. */
  structureTitle?: string;
  /** 팀 명단 화면 제목(h1과 라우트 `title`). 생략하면 화면은 '학생 명단', 문서 제목은 앱 기본. */
  rosterTitle?: string;
  /**
   * 학급 생성·이름변경·삭제·이동(드래그)을 켠다. **기본은 꺼짐**(읽기 전용)이다 — 연동 단체에서도 로컬 팀은 편집된다. 외부 팀(`source: 'external'`)은 이 옵션과 무관하게 항상 읽기 전용이다(원본이 학교종이에 있다).
   */
  structureEditable?: boolean;
  /**
   * 팀 구조 화면에 "실시간/일괄" 저장 모드 토글을 켜고 **일괄 모드를 기본**으로 한다(변경을 모았다가 "저장"으로 한 번에 원자 반영). **기본은 꺼짐**(실시간 저장만)이다.
   * `structureEditable` 이 켜져 있을 때만 의미가 있다.
   */
  structureSaveModes?: boolean;
  /** 팀 구조 트리의 깊이 상한(최상위=0). 예: 학년-학급 2단계 고정이면 1. 생략하면 무제한. */
  structureMaxDepth?: number;
  /** 단체를 처음 불러올 때 첫 최상위 팀만 펼치고 나머지는 접는다. 기본은 모두 펼침. */
  structureCollapseInitially?: boolean;
  /** 화면 문구에서 팀을 부르는 말(기본 '학급'). */
  structureTeamNoun?: string;
  /**
   * 팀 명단 화면(`teams/:teamId/members`)을 함께 노출한다. 기본은 켜짐. 끄면 명단 라우트를 만들지 않고 팀 구조의 이름 링크도 없앤다 — 팀 명단을 자기 화면으로 따로 가진 앱이 쓴다.
   */
  roster?: boolean;
  /**
   * 로컬 팀의 명단에서 기록용(record-only) 학생 추가·삭제를 켠다. **기본은 꺼짐**이다 — 대상이 이 서비스에 로그인하지 않는 문자 발송용 명단(sms)처럼 이름·전화번호만 기록하는 앱만 켠다.
   */
  rosterAddMember?: boolean;
}

/**
 * 팀(학급) 구조·팀 명단 화면 라우트. **내부 구현이다** — 앱은 직접 부르지 않고 `orgSettingsRoutes({ teams })` 옵션으로 켠다(2026-10 공개 export 제거).
 *
 * ```ts
 * { path: 'orgs/:orgId', children: [...orgSettingsRoutes({ teams: { structureEditable: true } }), ...앱 고유 라우트] }
 * ```
 *
 * - `orgs/:orgId/teams` — 팀 구조(트리). 팀 이름(쓰기 모드는 "명단" 아이콘)이 그 팀 명단으로 이동한다.
 * - `orgs/:orgId/teams/:teamId/members` — 한 팀의 명단(하위 팀 포함). 담임·상위 팀 Owner/Admin도 읽는다(권한은 서버가 판정).
 *
 * 모든 단체가 공유 계약(`SpinoffPlatformOrgContractService`)으로 읽는다 — 연동 단체는 서버가 학교종이 항목을 합성해 돌려준다. `teams/:teamId`(팀 입장 주소, `orgTeamEntryRoute`)와는 세 라우트 모두
 * `pathMatch: 'full'` 이라 순서와 무관하게 겹치지 않는다(`orgTeamEntryRoute` 는 기본값으로 `pathMatch: 'full'` 을 붙인다). 전제는 `orgSettingsRoutes()`와 같다 — 앱이 `withComponentInputBinding()`과 `paramsInheritanceStrategy: 'always'`를 켜고, 생성
 * 클라이언트의 상대 경로(`/orgs/...`) 요청이 API 오리진으로 향하도록 구성한다.
 */
export function orgTeamRoutes(options: OrgTeamRoutesOptions = {}): Route[] {
  const roster = options.roster ?? true;
  return [
    {
      path: 'teams',
      pathMatch: 'full',
      title: options.structureTitle,
      data: {
        ...(options.structureTitle ? { pageTitle: options.structureTitle } : {}),
        structureEditable: options.structureEditable ?? false,
        ...(options.structureSaveModes ? { structureSaveModes: true } : {}),
        ...(options.structureMaxDepth !== undefined ? { structureMaxDepth: options.structureMaxDepth } : {}),
        ...(options.structureCollapseInitially ? { structureCollapseInitially: true } : {}),
        ...(options.structureTeamNoun ? { structureTeamNoun: options.structureTeamNoun } : {}),
        ...(roster ? {} : { structureRosterLinks: false }),
      },
      loadComponent: () => import('./org-team-structure.component').then((m) => m.OrgTeamStructureComponent),
    },
    ...(roster
      ? [
          {
            path: 'teams/:teamId/members',
            pathMatch: 'full',
            title: options.rosterTitle,
            data: { ...(options.rosterTitle ? { pageTitle: options.rosterTitle } : {}), rosterAddMember: options.rosterAddMember ?? false },
            loadComponent: () => import('./org-team-roster.component').then((m) => m.OrgTeamRosterComponent),
          } satisfies Route,
        ]
      : []),
  ];
}
